/**
 * Persona Engine v3.0 Graduation Dashboard
 *
 * Employee-only admin page showing aggregate graduation statistics
 * derived from the Persona Engine health endpoint and generation log data.
 * Includes stage distribution pie chart, dimensions-derived bar chart,
 * and a generation log table with graduation fields.
 */

import { useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  GraduationCap,
  Layers,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react'
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { PageHeader, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { usePersonaEngineHealth, usePersonaEngineLog, usePersonaEngineInstances } from '@/hooks/usePersonaEngine'
import type { EngineType } from '@personic/shared/types/persona-engine'
import { ENGINE_SHORT_LABELS } from '@personic/shared/types/persona-engine'

// ─── Constants ───────────────────────────────────────────────────────────────

const STAGE_COLORS: Record<string, string> = {
  base: '#9ca3af',      // gray
  emerging: '#f59e0b',   // amber
  established: '#3b82f6', // blue
  mature: '#10b981',     // green
}

const STAGE_LABELS: Record<string, string> = {
  base: 'Base',
  emerging: 'Emerging',
  established: 'Established',
  mature: 'Mature',
}

const STAGE_ORDER = ['base', 'emerging', 'established', 'mature']

// ─── Sub-components ──────────────────────────────────────────────────────────

interface StatCardProps {
  label: string
  value: string | number
  icon: React.ElementType
  description: string
  variant: 'copper' | 'emerald' | 'amber' | 'blue'
}

function StatCard({ label, value, icon: Icon, description, variant }: StatCardProps) {
  const styles: Record<string, { bg: string; icon: string }> = {
    copper: { bg: 'bg-copper-50', icon: 'text-copper-500' },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600' },
  }
  const s = styles[variant] || styles.copper

  return (
    <motion.div
      whileHover={{ y: -2, boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}
      className="p-5 rounded-xl border border-warm-200 bg-white relative overflow-hidden"
    >
      <div className="absolute -right-3 -top-3 w-20 h-20 bg-warm-50/50 rounded-full" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${s.icon}`} />
          </div>
        </div>
        <p className="text-sm text-warm-500 font-medium">{label}</p>
        <motion.p
          key={String(value)}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-midnight-800 mt-1"
        >
          {value}
        </motion.p>
        <p className="text-xs text-warm-400 mt-1.5">{description}</p>
      </div>
    </motion.div>
  )
}

function ChartCard({ title, icon: Icon, children }: {
  title: string
  icon: React.ElementType
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-warm-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-copper-500" />
        <h4 className="text-sm font-semibold text-midnight-800">{title}</h4>
      </div>
      <div className="h-[280px]">{children}</div>
    </div>
  )
}

function StageBadge({ stage }: { stage: string }) {
  const colorMap: Record<string, string> = {
    base: 'bg-gray-100 text-gray-700',
    emerging: 'bg-amber-100 text-amber-700',
    established: 'bg-blue-100 text-blue-700',
    mature: 'bg-emerald-100 text-emerald-700',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorMap[stage] || 'bg-gray-100 text-gray-600'}`}>
      {STAGE_LABELS[stage] || stage}
    </span>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function GraduationDashboard() {
  const breadcrumbs = buildBreadcrumbs('/admin/persona-engine/graduation')

  const { data: health, isLoading: healthLoading, error: healthError } = usePersonaEngineHealth()
  const { data: logData, isLoading: logLoading, error: logError } = usePersonaEngineLog({ limit: 200 })
  const { data: instances, isLoading: instancesLoading } = usePersonaEngineInstances()

  const isLoading = healthLoading || logLoading || instancesLoading
  const error = healthError || logError

  // Derive graduation stats from instances (which have graduation_stage / graduation_dimensions_derived)
  const stageDistribution = useMemo(() => {
    if (!instances || instances.length === 0) {
      return STAGE_ORDER.map(stage => ({
        name: STAGE_LABELS[stage],
        value: 0,
        color: STAGE_COLORS[stage],
        key: stage,
      }))
    }

    const counts: Record<string, number> = { base: 0, emerging: 0, established: 0, mature: 0 }
    for (const inst of instances) {
      const stage = inst.graduation_stage || 'base'
      counts[stage] = (counts[stage] || 0) + 1
    }

    return STAGE_ORDER.map(stage => ({
      name: STAGE_LABELS[stage],
      value: counts[stage] || 0,
      color: STAGE_COLORS[stage],
      key: stage,
    }))
  }, [instances])

  // Derive dimensions-derived distribution from instances
  const dimensionsDistribution = useMemo(() => {
    const bins = Array.from({ length: 10 }, (_, i) => ({
      dimensions: String(i),
      count: 0,
    }))

    if (instances && instances.length > 0) {
      for (const inst of instances) {
        const derived = inst.graduation_dimensions_derived ?? 0
        const idx = Math.min(derived, 9)
        bins[idx].count++
      }
    }

    return bins
  }, [instances])

  // Stage count summaries
  const stageCounts = useMemo(() => {
    const counts = { base: 0, emerging: 0, established: 0, mature: 0 }
    if (instances) {
      for (const inst of instances) {
        const stage = (inst.graduation_stage || 'base') as keyof typeof counts
        if (stage in counts) counts[stage]++
      }
    }
    return counts
  }, [instances])

  const totalInstances = instances?.length ?? 0

  // Generation log entries for the table — the API may include graduation fields
  const logEntries = logData?.entries ?? []

  // ─── Loading skeleton ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Graduation Dashboard"
          description="Aggregate graduation statistics across all Persona Engine instances."
          breadcrumbs={breadcrumbs}
        />
        <div className="space-y-6 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-[140px] rounded-xl border border-warm-200 bg-warm-50 animate-pulse"
              />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className="h-[340px] rounded-xl border border-warm-200 bg-warm-50 animate-pulse"
              />
            ))}
          </div>
          <div className="h-[300px] rounded-xl border border-warm-200 bg-warm-50 animate-pulse" />
        </div>
      </div>
    )
  }

  // ─── Error state ─────────────────────────────────────────────────────────

  if (error) {
    return (
      <div>
        <PageHeader
          title="Graduation Dashboard"
          description="Aggregate graduation statistics across all Persona Engine instances."
          breadcrumbs={breadcrumbs}
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-16 text-center"
        >
          <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-midnight-700">Failed to load graduation data</h3>
          <p className="text-sm text-warm-500 mt-1 max-w-sm">
            {(error as Error).message || 'An unexpected error occurred while loading Persona Engine graduation statistics.'}
          </p>
        </motion.div>
      </div>
    )
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader
        title="Graduation Dashboard"
        description="Aggregate graduation statistics across all Persona Engine instances."
        breadcrumbs={breadcrumbs}
      />

      <motion.div variants={fadeUp} initial="hidden" animate="visible" className="space-y-6 max-w-7xl">

        {/* Summary stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Graduation Enabled"
            value={health?.graduation_enabled ? 'Active' : 'Disabled'}
            icon={health?.graduation_enabled ? CheckCircle2 : XCircle}
            description="System-wide graduation feature status"
            variant={health?.graduation_enabled ? 'emerald' : 'amber'}
          />
          <StatCard
            label="Total Instances"
            value={totalInstances.toLocaleString()}
            icon={Layers}
            description="All Persona Engine instances"
            variant="copper"
          />
          <StatCard
            label="Mature Instances"
            value={stageCounts.mature.toLocaleString()}
            icon={GraduationCap}
            description={`${totalInstances > 0 ? ((stageCounts.mature / totalInstances) * 100).toFixed(1) : 0}% of all instances`}
            variant="emerald"
          />
          <StatCard
            label="In Progress"
            value={(stageCounts.emerging + stageCounts.established).toLocaleString()}
            icon={Clock}
            description={`${stageCounts.emerging} emerging + ${stageCounts.established} established`}
            variant="blue"
          />
        </div>

        {/* Stage distribution mini-cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {STAGE_ORDER.map((stage) => (
            <div
              key={stage}
              className="rounded-xl border border-warm-200 bg-white p-4 flex items-center gap-3"
            >
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: STAGE_COLORS[stage] }}
              />
              <div className="min-w-0">
                <p className="text-xs text-warm-500 font-medium">{STAGE_LABELS[stage]}</p>
                <p className="text-xl font-bold text-midnight-800">
                  {stageCounts[stage as keyof typeof stageCounts] ?? 0}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Graduation stage pie chart */}
          <ChartCard title="Graduation Stage Distribution" icon={PieChartIcon}>
            {totalInstances === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <Activity className="w-10 h-10 text-warm-300 mb-3" />
                <p className="text-sm text-warm-500">No instances to display</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stageDistribution.filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={95}
                    dataKey="value"
                    nameKey="name"
                    paddingAngle={2}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {stageDistribution.filter(d => d.value > 0).map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #e5e0db',
                      fontSize: 12,
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(value: string) => (
                      <span className="text-midnight-700">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Dimensions derived bar chart */}
          <ChartCard title="Dimensions Derived Distribution" icon={BarChart3}>
            {totalInstances === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <Activity className="w-10 h-10 text-warm-300 mb-3" />
                <p className="text-sm text-warm-500">No instances to display</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dimensionsDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e0db" />
                  <XAxis
                    dataKey="dimensions"
                    tick={{ fontSize: 11 }}
                    stroke="#a3998e"
                    label={{ value: 'Dimensions Derived', position: 'insideBottom', offset: -2, fontSize: 11, fill: '#a3998e' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    stroke="#a3998e"
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #e5e0db',
                      fontSize: 12,
                    }}
                    formatter={(value: number) => [value, 'Instances']}
                    labelFormatter={(label: string) => `${label} dimensions`}
                  />
                  <Bar
                    dataKey="count"
                    name="Instances"
                    fill="#c27c3e"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        {/* Generation log table */}
        <div className="rounded-xl border border-warm-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-warm-200">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-copper-500" />
              <h4 className="text-sm font-semibold text-midnight-800">Recent Generation Log</h4>
              <span className="text-xs text-warm-400 ml-auto">
                {logData?.total ?? 0} total entries
              </span>
            </div>
          </div>

          {logEntries.length === 0 ? (
            <div className="py-12 text-center">
              <Activity className="w-10 h-10 text-warm-300 mx-auto mb-3" />
              <p className="text-sm text-warm-500">No generation log entries yet.</p>
              <p className="text-xs text-warm-400 mt-1">
                Entries will appear here once Persona Engine computations have been run.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-warm-200 bg-warm-50/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-midnight-600 uppercase tracking-wider">Engine</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-midnight-600 uppercase tracking-wider">Confidence</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-midnight-600 uppercase tracking-wider">Stage</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-midnight-600 uppercase tracking-wider">Dims Derived</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-midnight-600 uppercase tracking-wider">Archetype</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-midnight-600 uppercase tracking-wider">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-warm-100">
                  {logEntries.map((entry) => {
                    // The API response may include graduation_stage / dimensions_derived
                    const extEntry = entry as typeof entry & {
                      graduation_stage?: string | null
                      dimensions_derived?: number | null
                    }
                    return (
                      <tr key={entry.id} className="hover:bg-warm-50/30 transition-colors">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-copper-50 text-copper-700 text-xs font-medium">
                            {ENGINE_SHORT_LABELS[entry.engine_name as EngineType] ?? entry.engine_name.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-midnight-700 tabular-nums">
                          {entry.engine_confidence_score.toFixed(3)}
                        </td>
                        <td className="px-4 py-3">
                          {extEntry.graduation_stage ? (
                            <StageBadge stage={extEntry.graduation_stage} />
                          ) : (
                            <span className="text-warm-400 text-xs">--</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-midnight-700 tabular-nums">
                          {extEntry.dimensions_derived != null ? extEntry.dimensions_derived : (
                            <span className="text-warm-400 text-xs">--</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {entry.archetype_id ? (
                            <span className="text-midnight-600 text-xs font-mono">{entry.archetype_id}</span>
                          ) : (
                            <span className="text-warm-400 text-xs">none</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-warm-500 text-xs tabular-nums">
                          {entry.generation_timestamp
                            ? new Date(entry.generation_timestamp).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '--'
                          }
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </motion.div>
    </div>
  )
}
